import { Head, Link } from "@inertiajs/react"
import { Archive, ChevronRight, Zap } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { AutomationSummary } from "@/types/automation"

const DESCRIPTION =
  "Define the automations staff can turn on — the default wording and the roster query that decides who each one reaches."

export default function AdminAutomationsIndex({
  automations,
  archived_count,
}: {
  automations: AutomationSummary[]
  archived_count: number
}) {
  return (
    <>
      <Head title="Automations">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Automations" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <PageHeader
          title="Automations"
          description="An automation is a saved roster query plus the wording every organization starts from. Creating one here makes it available to staff; it does not turn it on for anyone."
          actions={
            <>
            {archived_count > 0 && (
              <Button asChild variant="ghost">
                <Link href="/admin/automations/archived" className="no-underline">
                  <Archive className="h-4 w-4" />
                  Archived ({archived_count})
                </Link>
              </Button>
            )}
            <Button asChild>
              <Link href="/admin/automations/new" className="no-underline">
                New automation
              </Link>
            </Button>
            </>
          }
        />

        <div className="mt-6">
          <FlashNotice />

          {automations.length === 0 ? (
            <div className="callout">
              <p className="text-sm text-ink-muted">
                No automations yet. Create one to give staff something to turn on.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
              {automations.map((automation) => (
                <li key={automation.id}>
                  <Link
                    href={`/admin/automations/${automation.id}/edit`}
                    className="flex items-center gap-3 px-4 py-3 no-underline hover:bg-surface"
                  >
                    <Zap className="h-4 w-4 shrink-0 text-ink-muted" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-ink-display">
                        {automation.name}
                      </div>
                      <div className="truncate text-xs text-ink-muted">
                        {automation.description || "No description"}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-ink-muted">
                        {automation.default_send_time ?? "—"} ·{" "}
                        {automation.has_query
                          ? `${automation.variables.length} ${
                              automation.variables.length === 1
                                ? "variable"
                                : "variables"
                            }`
                          : "no query yet"}
                      </div>
                    </div>
                    {automation.iupat_only && <Badge tone="neutral">International only</Badge>}
                    <Badge tone={automation.is_active ? "accent" : "muted"}>
                      {automation.is_active ? "Active" : "Inactive"}
                    </Badge>
                    <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </AdminShell>
    </>
  )
}
